import { commonOrdersPostQuery } from '../../api/orders/shared';
import {
  extractNonHistoricalTicketIdFromState,
  extractSelectedTicketFromState,
  getSimplrAccountIdOfSelectedTicket,
} from '../../extractFromState';
import { getOrderingSystem, getSelectedShop } from '../../selectors/tools';

// PLEASE NOTE: This file is for the old system-specific interfaces using common endpoints
// It is being deprecated in favor of the common interface with common data structures
// For common interface async actions, use CommonAsyncOrderActions

/**
 * Fetch common cancel info
 * @param {object} options
 * @param {string} options.simplrAccountId
 * @param {string} options.orderId
 * @param {string} options.shopType
 * @param {?string} options.shopName
 * @return {Function}
 */

export const fetchCancelInfo = (options) => async (dispatch, getState) => {
  const { orderId } = options;

  const state = getState();

  const payload = {
    simplrAccountId: getSimplrAccountIdOfSelectedTicket(state),
    orderId,
    shopType: getOrderingSystem(state),
  };

  const shopName = getSelectedShop(state);

  if (shopName) {
    payload.shopName = shopName;
  }

  return await commonOrdersPostQuery('/cancelInfo', {}, payload);
};

/**
 * Fetch common calculate info
 * @param {object} options
 * @param {string} options.orderId
 * @param {array} options.refundItems
 * @return {Function}
 */

export const fetchCalculateInfo = (options) => async (dispatch, getState) => {
  const { orderId, refundItems = [], uniqueRequestUuid } = options;

  const state = getState();

  const payload = {
    simplrAccountId: getSimplrAccountIdOfSelectedTicket(state),
    orderId,
    shopType: getOrderingSystem(state),
    refundItems,
  };

  const shopName = getSelectedShop(state);

  if (shopName) {
    payload.shopName = shopName;
  }

  return await commonOrdersPostQuery('/calculate', {}, payload, undefined, uniqueRequestUuid);
};

/**
 * Fetch common refund info
 * @param {object} options
 * @param {string} options.simplrAccountId
 * @param {string} options.orderId
 * @param {string} options.shopType
 * @param {?string} options.shopName
 * @return {Function}
 */

export const fetchShippedInfo = (options) => async (dispatch, getState) => {
  const { orderId } = options;

  const state = getState();

  const payload = {
    simplrAccountId: getSimplrAccountIdOfSelectedTicket(state),
    orderId,
    shopType: getOrderingSystem(state),
  };

  const shopName = getSelectedShop(state);

  if (shopName) {
    payload.shopName = shopName;
  }

  return await commonOrdersPostQuery('/shippedItems', {}, payload);
};

/**
 * Perform a refund
 * @param {object} options
 * @param {string} options.simplrAccountId
 * @param {string} options.orderId
 * @param {?string} options.comment
 * @param {array} options.refundItems
 * @param {string} options.shopType
 * @param {?string} options.shopName
 * @return {Function}
 */
export const cancelRefund = (options) => async (dispatch, getState) => {
  const {
    timeOut,
    refundAmount,
    userId,
    addNotes,
    orderId,
    comment = null,
    refundItems = [],
    messageBody,
    shopName = null,
  } = options;

  let { conversationId } = options;

  const state = getState();

  if (!conversationId) {
    conversationId = extractNonHistoricalTicketIdFromState(state);
  }

  const payload = {
    refundAmount,
    conversationId,
    userId,
    comment,
    refundItems,
    messageBody,
    simplrAccountId: getSimplrAccountIdOfSelectedTicket(state),
    orderId: String(orderId),
    shopType: getOrderingSystem(state),
    selectedTicket: extractSelectedTicketFromState(state).simplrTicketNumber,
  };

  const selectedShop = getSelectedShop(state);

  // back-end expects shopName to either be a non-null string if it is passed or to be not passed at all
  if (shopName) {
    payload.shopName = shopName;
  } else if (selectedShop) {
    payload.shopName = selectedShop;
  }

  if (addNotes) {
    payload.addNotes = addNotes;
  }

  return await commonOrdersPostQuery('/cancelRefund', {}, payload, timeOut);
};

/**
 * Perform a refund shipped items
 * @param {object} options
 * @param {string} options.orderId
 * @param {?string} options.comment
 * @param {array} options.refundItems
 * @return {Function}
 */
export const refundShippedItems = (options) => async (dispatch, getState) => {
  const { orderId, comment = null, refundItems = [], addNotes, conversationId, timeOut } = options;

  const state = getState();

  const payload = {
    simplrAccountId: getSimplrAccountIdOfSelectedTicket(state),
    orderId,
    comment,
    refundItems,
    shopType: getOrderingSystem(state),
  };

  const shopName = getSelectedShop(state);
  if (shopName) {
    payload.shopName = shopName;
  }

  if (addNotes && conversationId) {
    payload.addNotes = addNotes;
    payload.conversationId = conversationId;
  }

  return commonOrdersPostQuery('/refundShippedItems', {}, payload, timeOut);
};

/**
 * Perform a partial refund for selected items
 * @param {{amount, refundItems: (*|[]), orderId: string, conversationId: *, shopName, comment: string, shopType: *, selectedTicket: *, userId: *, simplrAccountId: *}} options
 * @param {string} options.orderId
 * @param {?string} options.comment
 * @param {array} options.refundItems
 * @param {number} options.amount
 * @param {string} options.addNotes
 * @param {string} options.conversationId
 * @param {number} options.timeout
 * @return {Function}
 */
export const partialRefundSelectedItems = (options) => async (dispatch, getState) => {
  const { orderId, comment = '', refundItems = [], amount, addNotes, conversationId, timeOut } = options;

  const state = getState();

  const payload = {
    simplrAccountId: getSimplrAccountIdOfSelectedTicket(state),
    orderId,
    comment,
    refundItems,
    amount,
    addNotes,
    conversationId,
    shopType: getOrderingSystem(state),
  };

  const shopName = getSelectedShop(state);
  if (shopName) {
    payload.shopName = shopName;
  }

  return commonOrdersPostQuery('/partialRefund', {}, payload, timeOut);
};
